import { FiltersCombobox } from '@/components/custom/filters-combobox'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { FilterOption } from '@/lib/utils'
import { formatSortFieldName } from '@/lib/formatters'
import type { ColumnSort, SortingState } from '@tanstack/react-table'
import { ChevronDown, GripVertical, X } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface SortRowProps {
  selectedSort: ColumnSort
  filtersList?: FilterOption[]
  handleSortSelect?: (oldSort: string, newSortValue: SortingState) => void
  clearSorts?: (sortId?: string) => void
}

export function SortRow({
  selectedSort,
  filtersList,
  handleSortSelect,
  clearSorts,
}: SortRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: selectedSort.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const handleSortIdUpdate = (sortValue: string) => {
    const sortUpdated = [{ id: sortValue, desc: selectedSort.desc }]
    handleSortSelect?.(selectedSort.id, sortUpdated)
  }

  const handleSortDirectionUpdate = (desc: boolean) => {
    const sortUpdated = [{ id: selectedSort.id, desc }]
    handleSortSelect?.(selectedSort.id, sortUpdated)
  }

  const handleClearSort = () => {
    if (!clearSorts) return
    clearSorts(selectedSort.id)
  }

  // Get display label from filtersList or fall back to formatted field name
  const getSortLabel = (sortId: string) => {
    const option = filtersList?.find((f) => f.key === sortId)
    return option?.label ?? formatSortFieldName(sortId)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 w-full justify-between"
    >
      <Button
        variant="ghost"
        size="icon"
        {...attributes}
        {...listeners}
        className="h-auto w-auto p-0 cursor-grab active:cursor-grabbing touch-none hover:bg-transparent"
      >
        <GripVertical className="size-3.5 text-muted-foreground/50 hover:text-primary transition-colors" />
      </Button>
      <div className="flex items-center justify-between gap-2 w-full">
        <FiltersCombobox
          triggerTitle={getSortLabel(selectedSort.id)}
          searchPlaceholder="Sort By"
          emptyMessage="No sort found."
          triggerIcon={null}
          triggerIconAfter={
            <ChevronDown className="size-3.5 text-muted-foreground" />
          }
          filtersList={filtersList}
          onFilterSelect={handleSortIdUpdate}
          className="h-fit min-h-[28px] flex-1 py-1 text-xs bg-background/50 border-border"
          contentAlign="start"
        />
        <Select
          value={selectedSort.desc ? 'desc' : 'asc'}
          onValueChange={(value) => {
            handleSortDirectionUpdate(value === 'desc')
          }}
        >
          <SelectTrigger className="gap-1 h-[28px] w-[115px] py-1 px-3 text-xs bg-background [&_svg]:size-3.5 [&_svg]:text-muted-foreground">
            <SelectValue className="text-xs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="asc">Ascending</SelectItem>
            <SelectItem value="desc">Descending</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button
        variant="ghost"
        className="w-fit h-fit p-0 hover:bg-transparent"
        onClick={handleClearSort}
      >
        <X className="size-3.5 text-muted-foreground/50 hover:text-destructive transition-colors" />
      </Button>
    </div>
  )
}
